import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik'

import SingleMediaForm from './SingleMediaForm'
import CarouselMediaForm from './CarouselMediaForm'
import EditForm from '../../../../../../../features/components/Forms/EditForm'

import useEditAdPreview from '../../../../../../../features/hooks/useEditAdPreview'
import { usePurifiedFormik } from '../../../../../../../hooks/formHooks/usePurifiedFormik'

import { getCreative, getInitialValuesByFormat } from './helpers'
import { getAdFormat } from '../../../helpers'

import { clearUpdateAd, updateAd } from '../../../../../../../modules/actions/ads'
import {
  adUpdateErrorSelector,
  adUpdateIsLoadingSelector,
  adWasUpdatedSelector
} from '../../../../../../../modules/selectors/ads'
import { selectedFacebookAdAccountSelector } from '../../../../../../../modules/selectors/adAccounts'

import { getValidationSchema } from './validation'
import { initialFileValues, initialMediaValues } from '../../../../fields'
import { AD_FORMAT } from '../../../../../../../constants/ads'
import { FACEBOOK_PLATFORM } from '../../../../../../../constants/selectLists/platformList'
import { HISTORY_META_EDIT_SECTION } from '../../../../../../../constants/changeHistory'
import { MEDIA_TYPE } from '../../../../../../Google/AdForms/fields'
import { IMAGE_HASH, SINGLE_MEDIA_ITEM } from '../../../../../../ReusableFormFields/AdFileUpload/fields'

const MediaForm = ({ editItemData: originalAd, adValues, adFormat, isInternalAd, title, ...formProps }) => {
  const dispatch = useDispatch()

  const successUpdate = useSelector(adWasUpdatedSelector)
  const { id: selectedAdAccountId } = useSelector(selectedFacebookAdAccountSelector)

  const handleSubmit = useCallback(
    modifiedAdValues => {
      const modifiedAdFormat = getAdFormat(modifiedAdValues)

      const { [MEDIA_TYPE]: mediaType, [SINGLE_MEDIA_ITEM]: singleMediaItem } = modifiedAdValues
      const singleMediaItemImageHash = singleMediaItem?.[IMAGE_HASH]

      const { id, creative, ad_account_id: adAccountId } = originalAd
      const originalDegreeOfFreedomSpec = originalAd.creative.degrees_of_freedom_spec
      const adAccount = adAccountId || selectedAdAccountId

      const updateAdData = {
        ...originalAd,
        account: adAccount,
        creative: {
          account_id: selectedAdAccountId,
          ...(originalDegreeOfFreedomSpec && { degrees_of_freedom_spec: originalDegreeOfFreedomSpec }),
          ...creative,
          ...(originalAd.creative.image_hash &&
            // single image ad has image_hash field in 2 places: inside and outside creative object.
            // We need to update it in both places
            mediaType === 'image' &&
            singleMediaItemImageHash && {
              image_hash: singleMediaItemImageHash
            }),
          ...getCreative({
            values: modifiedAdValues,
            originalAd,
            adFormat: modifiedAdFormat,
            adAccountId: adAccount
          })
        },
        ...(isInternalAd && { internal: true }),
        meta: {
          [HISTORY_META_EDIT_SECTION]: title
        }
      }

      // Some tracking_specs leads to an error, so we completely remove it from payload when do PUT request
      delete updateAdData.tracking_specs

      // for media edits PUT method is used
      dispatch(updateAd(updateAdData, id, FACEBOOK_PLATFORM, { requestMethod: 'PUT' }))
    },
    [originalAd, isInternalAd, selectedAdAccountId, dispatch, title]
  )

  const validationSchema = getValidationSchema()

  const initialValues = useMemo(
    () => ({
      // file
      ...initialFileValues,
      ...initialMediaValues,
      ...getInitialValuesByFormat(adValues, adFormat)
    }),
    [adFormat, adValues]
  )

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: handleSubmit
  })
  const purifiedFormik = usePurifiedFormik(formik)
  const { values: modifiedAdValues } = purifiedFormik

  useEditAdPreview(modifiedAdValues)

  const handleClearUpdateAd = useCallback(() => {
    dispatch(clearUpdateAd())
  }, [dispatch])

  const modifiedAdFormat = getAdFormat(modifiedAdValues)

  const getFormByAdFormat = useCallback(() => {
    switch (modifiedAdFormat) {
      case AD_FORMAT.carousel:
      case AD_FORMAT.carouselCustom:
        return <CarouselMediaForm formik={purifiedFormik} />
      case AD_FORMAT.single:
      case AD_FORMAT.singleCustom:
      default:
        return <SingleMediaForm formik={purifiedFormik} adFormat={adFormat} />
    }
  }, [adFormat, modifiedAdFormat, purifiedFormik])

  return (
    <EditForm
      formik={purifiedFormik}
      method="post"
      initialValues={initialValues}
      successSubmit={successUpdate}
      clearEditItem={handleClearUpdateAd}
      errorSelector={adUpdateErrorSelector}
      isLoadingSelector={adUpdateIsLoadingSelector}
      submitText="Save"
      {...formProps}
    >
      {getFormByAdFormat()}
    </EditForm>
  )
}

export default MediaForm
